import React from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import { makeStyles } from "@mui/styles";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import Menu from "@mui/icons-material/Menu";
import CloseRounded from "@mui/icons-material/CloseRounded";
import styles from '../../styles/headerStyle.js';
import logoWhite from '../../assets/img/logo165x90white.png';
import logoBlack from '../../assets/img/logo165x90black.png';
import { useTranslation } from "react-i18next";
import { Box } from '@mui/material';
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";

const useStyles = makeStyles(styles);

export default function Header(props) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const classes = useStyles();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [lightMode, setLightMode] = React.useState(false);
  const settings = useSelector(state => state.settingsdata.settings);
  const { color, rightLinks, leftLinks, fixed, absolute, changeColorOnScroll } = props;

  const headerColorChange = React.useCallback(() => {
    if (!changeColorOnScroll) return;
    const headerEl = document.body.getElementsByTagName("header")[0];
    if (!headerEl) return;
    const windowsScrollTop = window.pageYOffset;
    const scrolled = windowsScrollTop > changeColorOnScroll.height;
    setLightMode(scrolled && (changeColorOnScroll.color === "conecta2Light" || changeColorOnScroll.color === "white"));
    if (scrolled) {
      headerEl.classList.remove(classes[color]);
      headerEl.classList.add(classes[changeColorOnScroll.color]);
    } else {
      headerEl.classList.add(classes[color]);
      headerEl.classList.remove(classes[changeColorOnScroll.color]);
    }
  }, [changeColorOnScroll, classes, color]);

  React.useEffect(() => {
    if (!changeColorOnScroll) return undefined;
    headerColorChange();
    window.addEventListener("scroll", headerColorChange, { passive: true });
    return () => {
      window.removeEventListener("scroll", headerColorChange);
    };
  }, [changeColorOnScroll, headerColorChange]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const appBarClasses = classNames({
    [classes.appBar]: true,
    [classes[color]]: color,
    [classes.absolute]: absolute,
    [classes.fixed]: fixed
  });

  const renderBrand = (light) => (
    <Button href="/" className={`${classes.title} ${classes.brandMark}`} aria-label="Conecta2 Multiservices">
      <img src={logoWhite} alt="Conecta2 Multiservices" className={classNames(classes.brandLogo, light && classes.brandLogoHidden)} />
      <img src={logoBlack} alt="" aria-hidden="true" className={classNames(classes.brandLogo, classes.brandLogoOverlay, !light && classes.brandLogoHidden)} />
    </Button>
  );
  const brandComponent = renderBrand(lightMode);

  const linksNode = rightLinks
    ? React.isValidElement(rightLinks)
      ? React.cloneElement(rightLinks, { light: lightMode })
      : rightLinks
    : null;
  const drawerLinksNode = rightLinks
    ? React.isValidElement(rightLinks)
      ? React.cloneElement(rightLinks, { mobile: true, onNavigate: () => setMobileOpen(false) })
      : rightLinks
    : null;

  return (
    <AppBar
      className={appBarClasses}
      color="transparent"
      elevation={0}
      position={fixed ? "fixed" : absolute ? "absolute" : "relative"}
    >
      {settings && settings.appName ? (
        <Helmet>
          <title>{settings.appName + " | " + t("book_your_title")}</title>
          <meta name="description" content={t("about_us_content2")} />
        </Helmet>
      ) : null}
      <Toolbar className={classes.container} style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
        {leftLinks !== undefined ? brandComponent : null}
        <div className={isRTL === 'rtl' ? null : classes.flex}>
          {leftLinks !== undefined ? (
            <Box component="div" sx={{ display: { sm: 'none', md: 'block' } }}>
              {leftLinks}
            </Box>
          ) : (
            brandComponent
          )}
        </div>

        <div style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
          <Box component="div" sx={{ display: { sm: 'none', md: 'block', xs: 'none' } }}>
            {linksNode}
          </Box>
        </div>
        <Box component="div" sx={{ display: { md: 'none', xs: 'block' } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            sx={{ color: lightMode ? '#1A0F3B' : '#FFFFFF' }}
          >
            <Menu />
          </IconButton>
        </Box>
      </Toolbar>
      <Box component="div" sx={{ display: { md: 'none', xs: 'block' } }}>
        <Drawer
          variant="temporary"
          anchor={"right"}
          open={mobileOpen}
          classes={{
            paper: classes.drawerPaper
          }}
          onClose={handleDrawerToggle}
        >
          <div className={classes.drawerHeader} style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
            {renderBrand(false)}
            <IconButton aria-label="close drawer" onClick={handleDrawerToggle} className={classes.drawerClose}>
              <CloseRounded />
            </IconButton>
          </div>
          <div className={classes.appResponsive} style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
            {leftLinks}
            {drawerLinksNode}
          </div>
          <div className={classes.drawerFooter}>
            En todo Colombia
            <strong>Juntos llegamos más lejos</strong>
          </div>
        </Drawer>
      </Box>
    </AppBar>
  );
}

Header.defaultProp = {
  color: "transparent"
};

Header.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "info",
    "success",
    "warning",
    "danger",
    "transparent",
    "white",
    "rose",
    "dark",
    "conecta2Dark",
    "conecta2Light",
    "header"
  ]),
  rightLinks: PropTypes.node,
  leftLinks: PropTypes.node,
  brand: PropTypes.string,
  fixed: PropTypes.bool,
  absolute: PropTypes.bool,
  changeColorOnScroll: PropTypes.shape({
    height: PropTypes.number.isRequired,
    color: PropTypes.oneOf([
      "primary",
      "info",
      "success",
      "warning",
      "danger",
      "transparent",
      "white",
      "rose",
      "dark",
      "conecta2Dark",
      "conecta2Light",
      "header"
    ]).isRequired
  })
};
